import React, { useState } from "react";
import { getUniversitySearchResult } from "api/search.api";
import { debounce } from "lodash";
import { createNameLabelArray } from "utils";
import SearchFilterDropdown from "components/Common/SearchableDropdown/SearchFilterDropdown";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/store";
import { addUniversity, removeUniversity } from "store/slices/searcSlice";
import { IKeywordOption } from "components/Filters/AddKeywords";

export interface ResponseData {
  value: string;
  label: string;
  extra?: string;
}
interface UniversitySelectProps {
  onSelect?: (selectedOption: ResponseData | null) => void;
  styles?: any;
  isBorder?: boolean;
  isSmall?: boolean;
}

const UniversitySelect: React.FC<UniversitySelectProps> = ({ onSelect, styles, isBorder, isSmall }) => {
  const [options, setOptions] = useState<ResponseData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const dispatch = useDispatch();

  const selectedUniversities = useSelector((state: RootState) => state.search.universities);

  const handleChange = (selected: IKeywordOption[] | null) => {
    const selectedValues = selected ? selected.map((option) => option.value) : [];
    const lastSelectedValue = selectedValues[selectedValues.length - 1];

    dispatch(addUniversity(lastSelectedValue));
  };

  const handleDeselect = (value: string | null) => {
    if (value) {
      dispatch(removeUniversity(value));
    }
  };

  // Fetch the industry list based on the input value (user's search)
  const fetchOptions = (inputValue: string) => {
    setLoading(true);
    getUniversitySearchResult(inputValue)
      .then((result: any) => {
        // eslint-disable-next-line no-debugger

        setOptions(createNameLabelArray(result));
      })
      .catch((error) => {
        console.error("Error fetching industry list:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Debounced function to avoid too many API calls
  const debouncedFetchOptions = debounce(fetchOptions, 300);

  // Handle input change (when user types in the search box)
  const handleInputChange = (inputValue: string) => {
    if (inputValue.length > 2) {
      debouncedFetchOptions(inputValue);
    }
  };

  return (
    <SearchFilterDropdown
      mode="multiple"
      options={options}
      loading={loading}
      onChange={(values: string[]) =>
        handleChange(values.map((value) => ({ value, label: value })))
      }
      defaultValue={selectedUniversities}
      handleDeselect={handleDeselect}
      onInputChange={handleInputChange}
      isBorder={isBorder}
      isSmall={isSmall}
    />
  );
};

export default UniversitySelect;
